import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  WorkflowTooltip,
  WorkflowTooltipWrap,
} from "@via/components/tooltip/workflow-tooltip.tsx";
import {
  HandleTypes,
  WorkflowEditTypes,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { getHandleClass } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { NODE_TYPES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers";
import { TooltipInput } from "@via/utils/interfaces.tsx";
import { Handle, NodeToolbar, Position, useReactFlow } from "@xyflow/react";
import { type JSX, memo, useMemo } from "react";

const InputNode = ({
  data,
  id,
  onDeleteInput,
  onEditInput,
  onHandleHover,
  onHandleLeave,
  isEditingAllowed,
}: any): JSX.Element => {
  const { getEdges, getNode } = useReactFlow();
  const edges = getEdges();
  const isSourceConnected = useMemo(
    () => edges.some(edge => edge.source === id),
    [edges]
  );
  // Check if a specific handle is connected
  const isHandleConnected = useMemo(
    () => (handle: string) => {
      return edges.some(
        edge => edge.source === id && edge.sourceHandle === handle
      );
    },
    [edges, id]
  );
  const isValidTargetConnection = (connection, file): boolean => {
    if (!connection) return false;

    const { target, targetHandle } = connection;
    const node = getNode(target);
    if (!node || node?.type === NODE_TYPES.OutputNode) return false;
    if (!isSourceConnected) {
      return true;
    }
    const inputHandles = node.data.inputHandles as unknown as string[];
    const tooltips = node.data.tooltips as unknown as {
      inputs: TooltipInput[];
    };

    const match = inputHandles.indexOf(targetHandle as string);
    if (match === -1 || match === undefined) return false;

    return tooltips.inputs[match]?.fileType === file;
  };
  return (
    <div className={`flex ${data.className}`}>
      <NodeToolbar
        className={classNames(
          "flex flex-row rounded-md border-[1px] border-gray-300 bg-white p-2",
          !isEditingAllowed ? "pr-1" : ""
        )}>
        <PencilIcon
          className="w-[20px] cursor-pointer pr-1 hover:text-gray-600"
          onClick={() => onEditInput(WorkflowEditTypes.EditInput, data)}
        />
        {isEditingAllowed && (
          <TrashIcon
            className="w-[20px] cursor-pointer pl-1 hover:text-gray-600"
            onClick={() => onDeleteInput(id)}
          />
        )}
      </NodeToolbar>
      <div title={data.label} className="pt-8 text-[13px]">
        {data.label?.length > 7 ? `${data.label.slice(0, 7)}...` : data.label}
      </div>
      {data?.outputHandles.map((handle, i) => {
        const handleConnected = isHandleConnected(handle);
        return (
          <WorkflowTooltipWrap key={`${i}-${handle}`}>
            <Handle
              id={handle}
              type="source"
              className={getHandleClass(
                data,
                data?.tooltips?.outputs[i]?.fileType,
                handle,
                HandleTypes.Source,
                id,
                handleConnected
              )}
              isValidConnection={connection =>
                isValidTargetConnection(
                  connection,
                  data?.tooltips?.outputs[i]?.fileType
                )
              }
              position={Position.Right}
              onMouseEnter={() =>
                onHandleHover(
                  data?.tooltips?.outputs[i]?.fileType,
                  HandleTypes.Source,
                  handle,
                  id,
                  NODE_TYPES.InputNode,
                  handleConnected
                )
              }
              onMouseLeave={onHandleLeave}>
              <WorkflowTooltip data={data?.tooltips?.outputs[i]} />
            </Handle>
          </WorkflowTooltipWrap>
        );
      })}
    </div>
  );
};
export default memo(InputNode);
